const DEFAULT_QUALITY = 0.82;
// Leave room for upload/storage layers that add JPEG metadata during saving.
const TARGET_SIZE_RATIO = 0.8;
const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MIME_EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

function loadImage(file) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => createImageBitmap(file));
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The image could not be decoded.'));
    };
    image.src = url;
  });
}

function fileNameForType(name, type) {
  const extension = MIME_EXTENSIONS[type];

  if (!extension) return name;

  const baseName = name.replace(/\.[^.]+$/, '');

  return `${baseName}.${extension}`;
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

function qualityCandidates(quality) {
  const normalized = Number.isFinite(Number(quality))
    ? Math.max(0.1, Math.min(1, Number(quality)))
    : DEFAULT_QUALITY;

  return [normalized, normalized * 0.8, normalized * 0.6, 0.4]
    .map((value) => Math.max(0.1, Math.min(1, value)))
    .filter((value, index, values) => values.indexOf(value) === index);
}

/**
 * Re-encode raster images at their original pixel dimensions before they reach FoF Upload.
 * Unsupported formats and failed decodes are intentionally returned unchanged.
 */
export async function compressImage(file, quality = DEFAULT_QUALITY) {
  if (!file || !COMPRESSIBLE_TYPES.has(file.type) || typeof document === 'undefined') return file;

  let image;

  try {
    image = await loadImage(file);

    const width = image.width || image.naturalWidth;
    const height = image.height || image.naturalHeight;

    if (!width || !height) return file;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');

    if (!context) return file;

    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    // PNG does not expose a useful quality setting in Canvas. WebP keeps the
    // same dimensions and supports transparency, so use it only when it is
    // actually smaller than the original PNG.
    const outputTypes = file.type === 'image/png' ? ['image/webp', 'image/png'] : [file.type];
    const qualities = qualityCandidates(quality);
    let blob = null;

    for (const outputType of outputTypes) {
      for (const candidateQuality of qualities) {
        const candidate = await canvasBlob(canvas, outputType, candidateQuality);

        if (candidate && candidate.size < file.size && (!blob || candidate.size < blob.size)) {
          blob = candidate;

          if (candidate.size <= file.size * TARGET_SIZE_RATIO) break;
        }
      }

      if (blob && blob.size <= file.size * TARGET_SIZE_RATIO) break;
    }

    if (!blob) return file;

    const outputType = blob.type || file.type;

    return new File([blob], fileNameForType(file.name, outputType), {
      type: outputType,
      lastModified: file.lastModified,
    });
  } catch (error) {
    return file;
  } finally {
    if (image && typeof image.close === 'function') image.close();
  }
}
